// Product rows carry the Xianyu / Goofish `source_url` so the backend can
// re-scrape them, but that URL must never become a shopper-facing destination:
// sending a customer to goofish.com hands the sale straight to the marketplace.
//
// Every product click in the storefront goes through here, so the only possible
// outcome is our own ProductDetail route. When a row is missing its categoryId
// (admin rows created from a URL without one store an empty string) we recover
// the ids from the source link instead of falling back to it, and ProductDetail
// happily loads with categoryId 0.

function extractIdsFromLink(link) {
  const out = { id: null, categoryId: null };
  if (!link || typeof link !== 'string') return out;

  const trimmed = link.trim();
  if (!trimmed) return out;

  try {
    const u = new URL(trimmed);
    const id = u.searchParams.get('id') || u.searchParams.get('itemId') || u.searchParams.get('item_id');
    const categoryId = u.searchParams.get('categoryId') || u.searchParams.get('category') || u.searchParams.get('cid');
    if (id) out.id = String(id);
    if (categoryId) out.categoryId = String(categoryId);

    // Path form: /item/<id>/<categoryId>
    if (!out.id) {
      const path = u.pathname.match(/\/item\/([^/]+)(?:\/([^/]+))?/);
      if (path) {
        out.id = path[1];
        if (!out.categoryId && path[2]) out.categoryId = path[2];
      }
    }
  } catch (e) {
    // Not a parseable URL; take the last long digit run, matching the
    // id extraction used on the backend.
    const matches = trimmed.match(/(\d{4,})/g);
    if (matches && matches.length) out.id = matches[matches.length - 1];
  }

  return out;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const str = String(value).trim();
    if (str) return str;
  }
  return null;
}

// Returns a vue-router location for the product, or null when the row carries
// no usable item id at all (in which case the card should simply not navigate).
function resolveProductRoute(item) {
  if (!item) return null;

  const fromLink = extractIdsFromLink(item.link || item.url || item.productLink || item.sourceUrl || item.source_url);

  const id = firstNonEmpty(item.id, item.itemId, item.productId, item.product_id, fromLink.id);
  if (!id) return null;

  const categoryId = firstNonEmpty(item.categoryId, item.category_id, fromLink.categoryId) || '0';

  return { name: 'ProductDetail', query: { id, categoryId } };
}

function openProduct(router, item) {
  const route = resolveProductRoute(item);
  if (!route || !router) return false;

  router.push(route).catch((err) => {
    if (err && err.name !== 'NavigationDuplicated') throw err;
  });
  return true;
}

const MARKETPLACE_HOST = /(^|\.)(goofish|xianyu|idlefish)\.com$/i;

function isMarketplaceUrl(link) {
  if (!link || typeof link !== 'string') return false;
  try {
    return MARKETPLACE_HOST.test(new URL(link.trim()).hostname);
  } catch (e) {
    return false;
  }
}

// Follows an admin-configured link (homepage slide CTAs). A marketplace URL that
// slipped into the CMS is rewritten to its on-site product page, and dropped
// outright if it carries no item id — the storefront never hands a shopper off
// to Xianyu, whatever is stored in the homepage config.
function openLink(router, link) {
  const url = String(link || '').trim();
  if (!url) return false;

  if (isMarketplaceUrl(url)) {
    return openProduct(router, { link: url });
  }

  window.location.href = url;
  return true;
}

const productNavigation = {
  extractIdsFromLink,
  resolveProductRoute,
  openProduct,
  isMarketplaceUrl,
  openLink,
};

module.exports = productNavigation;
module.exports.default = productNavigation;
